import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@radix-ui/themes';

import { setScrollPosition } from '../../../../lib/redux/campaignScroll/campaignScrollSlice';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

import Text from '../../../../shared/Text';
import { IncompleteTag } from './IncompleteTag.jsx';
import { TileCardMenu } from './TileCardMenu.jsx';
import { TileImage } from './TileImage.jsx';
import styles from './TileCard.module.css';

const TileContainer = ({ tile, children, showIncompleteBorder }) => {
  const { t } = useTranslation('tiles');

  const disabledDnd = tile.status === 'archived';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: tile.id,
    data: tile,
    disabled: disabledDnd,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Container
      title={disabledDnd ? t('disabledDnd') : ''}
      ref={setNodeRef}
      key={tile.id}
      id={tile.id}
      style={style}
      isDragging={isDragging}
      showIncompleteBorder={showIncompleteBorder && !tile.fullyAssembled}
      tileStatus={tile.tileStatus}
      {...attributes}
      {...listeners}
    >
      {children}{' '}
    </Container>
  );
};

export const TileCard = ({
  tile,
  campaignId,
  showIncompleteBorder,
  postStatus,
}) => {
  const { thumbnail, ribbonTagName, ribbonTagColor } = tile;

  const dispatch = useDispatch();
  const location = useLocation();

  const { campaign } = useCampaignContext();

  const baseTileLink = campaign.isArchived
    ? `tile/${tile.id}`
    : `edit/${tile.id}`;
  const tileLink = location.search
    ? `${baseTileLink}${location.search}`
    : baseTileLink;

  return (
    <TileContainer tile={tile} showIncompleteBorder={showIncompleteBorder}>
      {tile.audienceColor && (
        <Tooltip content={tile.audienceName}>
          <div className={styles.campaignTagWrapper}>
            <div
              className={styles.campaignTag}
              style={{ backgroundColor: tile.audienceColor }}
            />
          </div>
        </Tooltip>
      )}
      <ActionLink
        to={tileLink}
        onClick={() =>
          dispatch(
            setScrollPosition({ page: 'edit', scrollPosition: window.scrollY }),
          )
        }
      >
        <TileImage
          thumbnail={thumbnail}
          ribbonTagName={ribbonTagName}
          ribbonTagColor={ribbonTagColor}
          tileStatus={tile.tileStatus}
        />
        {!tile.fullyAssembled && <IncompleteTag />}
        <Headline variant="small1">{tile.headline}</Headline>
      </ActionLink>
      <TileStatsContainer>
        <UniqueCount>
          <UserIcon />
          <Text color="black.80">{tile.uniqueViews}</Text>
        </UniqueCount>
        <ViewCount>
          <EyeIcon />
          <Text color="black.80">{tile.totalViews}</Text>
        </ViewCount>
        <ClickCount>
          <HandPointIcon />
          <Text color="black.80">{tile.tileCompletionsCount}</Text>
        </ClickCount>
      </TileStatsContainer>
      <ButtonsContainer>
        <TileCardMenu
          tileId={tile.id}
          tileHeadline={tile.headline}
          campaignId={campaignId}
          tileStatus={tile.tileStatus}
          postStatus={postStatus}
        />
      </ButtonsContainer>
    </TileContainer>
  );
};

const ButtonsContainer = styled.div`
  position: absolute;
  top: 5px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
`;

const Container = styled.div`
  border-radius: 8px;
  background: ${themeGet('colors.white')};
  border: ${props =>
    props.showIncompleteBorder
      ? '1px solid #FF9385;'
      : themeGet('colors.black.10')};
  cursor: grab;
  position: relative;
  opacity: ${({ isDragging }) => (isDragging ? 0 : 1)};
  width: 190px;
  box-shadow: 0 2px 3px rgb(0 0 0 / 22%);

  ${ButtonsContainer} {
    visibility: hidden;
  }

  &:hover ${ButtonsContainer} {
    visibility: visible;
  }

  filter: grayscale(${({ tileStatus }) => (tileStatus === 'archive' ? 1 : 0)});
`;

const ActionLink = styled(Link)`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 192px;
  width: 190px;
`;

const Headline = styled(Text)`
  padding: 10px;
  text-align: left;
`;

const TileStatsContainer = styled.div`
  display: flex;
  padding: 0 0 10px 10px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${themeGet('colors.black.80')};
`;

const ViewCount = styled.div`
  margin-right: 11px;
`;
const UniqueCount = styled(ViewCount)``;
const ClickCount = styled.div``;

const UserIcon = styled.i.attrs({
  className: 'fa-light fa-user',
})`
  margin-right: 2px;
`;

const EyeIcon = styled.i.attrs({
  className: 'fa-light fa-eye',
})`
  margin-right: 2px;
`;

const HandPointIcon = styled.i.attrs({
  className: 'fa-light fa-hand-pointer',
})`
  margin-right: 2px;
`;
